import type { Response } from "~/types";
import { z } from "zod";

export function useGiftCard() {
  const localePath = useLocalePath();

  const { t } = useI18n();
  const { apiVersion } = useRuntimeConfig().public;

  const {
    selectedLocation,
    selectedPaymentMethod,
    isNavigating,
    payGiftCardLoading,
    giftCardSelected,
    giftCardCodeRef,
    invoice,
  } = storeToRefs(useBusinessStore());

  const prepareFormData = () => {
    const fd = new FormData();
    const isGift = giftCardSelected.value?.recipient?.id === "gift";

    fd.append("amount", String(giftCardSelected.value?.price || ""));

    if (isGift) {
      fd.append(
        "sender_name",
        giftCardSelected.value?.recipient?.form?.sender_name || ""
      );
      fd.append(
        "recipient_name",
        giftCardSelected.value?.recipient?.form?.recipient_name || ""
      );
      fd.append(
        "recipient_email",
        giftCardSelected.value?.recipient?.form?.recipient_email || ""
      );
      fd.append(
        "personal_message",
        giftCardSelected.value?.recipient?.form?.personal_message || ""
      );
    }

    fd.append("payment_method_id", selectedPaymentMethod.value?.id.toString());

    if (selectedPaymentMethod.value?.key === "tap-cc")
      fd.append("tap_token", selectedPaymentMethod.value?.tap_token as string);

    if (selectedPaymentMethod.value?.accept_invoice && invoice.value)
      fd.append("invoice", invoice.value as File);

    return fd;
  };

  const payGiftCard = async () => {
    try {
      payGiftCardLoading.value = true;
      const response: Response<{
        redirect_url: string | null;
        gift_card_id: string;
      }> = await $larafetch(
        `/api/${apiVersion}/${selectedLocation.value?.identifier}/new-gift-card`,
        {
          method: "post",
          body: prepareFormData(),
        }
      );

      if (response.code !== 200) return;

      isNavigating.value = true;

      SuccessToast(response.message);

      if (selectedPaymentMethod.value?.redirect && response.data.redirect_url) {
        await navigateTo(response.data.redirect_url, { external: true });
        return;
      }

      await navigateTo(
        localePath(
          `/${selectedLocation.value?.identifier}/gift-cards/finished?gift_card_id=${response.data.gift_card_id}`
        )
      );
    } catch (err) {
      console.error(err);
    } finally {
      payGiftCardLoading.value = false;
    }
  };

  const giftCardCodeSchema = z.object({
    gift_card_code: z
      .string({
        required_error: t("validation.required", {
          name: t("label.gift_card_code"),
        }),
      })
      .min(8, t("validation.min", { name: t("label.gift_card_code"), min: 8 }))
      .max(8, t("validation.max", { name: t("label.gift_card_code"), max: 8 })),
  });

  const checkGiftCardCodeSchema = async () => {
    const form = giftCardCodeRef.value;
    if (!form) return false;

    return await form.validate();
  };

  return {
    payGiftCard,
    giftCardCodeSchema,
    checkGiftCardCodeSchema,
  };
}
